export const COOKIE_CONSENT_SCRIPT_ID = 'CookieConsent';

export const COOKIE_CONSENT_SCRIPT_SRC =
  'https://policy.app.cookieinformation.com/uc.js';

/**
 * Appends the Cookie Information script to <head>, with attributes identical
 * to the ones the vendor supplied — loading semantics necessarily differ,
 * since a dynamically created script is unavoidably injected after page
 * parsing has already started. Idempotent: a second call while the element
 * exists is a no-op.
 *
 * Deliberately NOT in the served HTML: the script is injected only once the
 * domicile modal is no longer blocking (see gatsby-browser.js and
 * docs/superpowers/specs/2026-08-13-consent-sequencing-conditional-load-design.md).
 */
export function loadCookieConsent(): void {
  if (document.getElementById(COOKIE_CONSENT_SCRIPT_ID)) {
    return;
  }
  const script = document.createElement('script');
  script.id = COOKIE_CONSENT_SCRIPT_ID;
  script.src = COOKIE_CONSENT_SCRIPT_SRC;
  script.setAttribute('data-culture', 'EN');
  script.setAttribute('data-gcm-version', '2.0');
  script.type = 'text/javascript';
  // A document.createElement('script') defaults to async=true once it has a
  // src, unlike a script parsed from HTML markup. Force it back off to keep
  // in-order, blocking execution relative to any other dynamically inserted
  // script (there are none today, but this is the vendor's documented
  // expectation, and it costs nothing to honour).
  script.async = false;
  document.head.appendChild(script);
}

/**
 * The privacy policy must stay readable before any consent decision, so the
 * consent banner never shows there (see gatsby-browser.js). The trailing
 * slash is tolerated because static hosting may serve either form.
 */
export const CONSENT_EXEMPT_PATH = '/en/privacy_policy';

export function isConsentExemptPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === CONSENT_EXEMPT_PATH;
}

/** Cookie Information stores the visitor's decision in this cookie. */
export const CONSENT_COOKIE_NAME = 'CookieInformationConsent';

/** Cookie Information dispatches this window event after any decision. */
export const CONSENT_GIVEN_EVENT = 'CookieInformationConsentGiven';

export function hasConsentDecision(
  cookieString: string = typeof document === 'undefined' ? '' : document.cookie,
): boolean {
  return cookieString
    .split(';')
    .some((part) => part.trim().startsWith(`${CONSENT_COOKIE_NAME}=`));
}
